import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ShieldCheck,
  GitBranch,
  FileCheck2,
  AlertTriangle,
  Copy,
  ExternalLink,
  Package,
  CheckCircle2,
} from 'lucide-react';
import { Button } from './ui/button';
import { ScrollArea } from './ui/scroll-area';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * BuildAttestationPanel — "verify us, don't trust us".
 *
 * Fetches the live deployment's build attestation, shows the git
 * commit + image digest + Rekor transparency-log entry, and renders
 * a copy-pasteable `cosign verify` command an auditor can run
 * locally. When verified=true, we show a green chip; when false
 * (local dev, unsigned image, or tampered attestation), we show an
 * amber warning saying "this is an unverified build".
 *
 * Used on `/manifesto` and as a standalone surface at `/verify`.
 */
export const BuildAttestationPanel = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copiedField, setCopiedField] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/build-attestation`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const j = await res.json();
        setData(j);
      } catch (e) {
        setError(e.message || 'unreachable');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const copyTo = async (text, field) => {
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 1500);
    } catch (_e) { /* noop */ }
  };

  if (loading) {
    return (
      <section
        className="max-w-5xl mx-auto px-6 py-12 lg:py-20"
        data-testid="build-attestation-panel-loading"
      >
        <div className="text-sm text-slate-500">Fetching build attestation…</div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="max-w-5xl mx-auto px-6 py-12 lg:py-20">
        <div className="text-sm text-red-300 bg-red-500/10 border border-red-500/20 rounded-lg p-3">
          Could not fetch build attestation: {error}
        </div>
      </section>
    );
  }

  const verified = !!data?.verified;
  const shortCommit = (data?.git_commit || 'unknown').slice(0, 12);
  const shortDigest = (data?.image_digest || 'unknown').slice(0, 19);

  return (
    <section
      id="build"
      className="max-w-5xl mx-auto px-6 py-12 lg:py-20"
      data-testid="build-attestation-panel"
    >
      <div className={`rounded-3xl border p-8 lg:p-12 ${
        verified
          ? 'border-emerald-500/30 bg-emerald-500/5'
          : 'border-amber-500/30 bg-amber-500/5'
      }`}>
        <div className="flex items-center gap-3 mb-4">
          <Package className="w-6 h-6 text-emerald-400" strokeWidth={1.75} />
          <h2
            className="text-2xl lg:text-3xl font-bold text-white tracking-tight"
            style={{ fontFamily: 'Outfit, sans-serif' }}
          >
            Verify the running build.
          </h2>
        </div>
        <p className="text-base text-slate-300 leading-relaxed max-w-3xl mb-6">
          Every push to <code className="text-xs text-emerald-300">main</code>
          {' '}produces a bit-identical Docker image from pinned sources. The
          image is signed with Sigstore keyless and its attestation is
          recorded on the public Rekor transparency log. An auditor can
          rebuild from source, verify the signature, and confirm that the
          binary running this deployment is the source published on GitHub —
          without trusting us.
        </p>

        {/* Verified / Unverified chip */}
        <div className="mb-6 flex items-center gap-2">
          {verified ? (
            <span
              className="inline-flex items-center gap-2 text-sm text-emerald-300 bg-emerald-500/10 border border-emerald-500/30 px-3 py-1 rounded-full"
              data-testid="build-attestation-verified-chip"
            >
              <CheckCircle2 className="w-4 h-4" />
              Signed, reproducible build
            </span>
          ) : (
            <span
              className="inline-flex items-center gap-2 text-sm text-amber-300 bg-amber-500/10 border border-amber-500/30 px-3 py-1 rounded-full"
              data-testid="build-attestation-unverified-chip"
            >
              <AlertTriangle className="w-4 h-4" />
              This deployment has not published a signed attestation
            </span>
          )}
        </div>

        {/* Field grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <FieldRow
            label="Git commit"
            icon={<GitBranch className="w-3.5 h-3.5" />}
            value={data.git_commit}
            displayValue={shortCommit}
            href={data.source_tree_url}
            testid="build-attestation-commit"
            onCopy={() => copyTo(data.git_commit, 'commit')}
            copied={copiedField === 'commit'}
          />
          <FieldRow
            label="Image digest"
            icon={<FileCheck2 className="w-3.5 h-3.5" />}
            value={data.image_digest}
            displayValue={shortDigest}
            testid="build-attestation-digest"
            onCopy={() => copyTo(data.image_digest, 'digest')}
            copied={copiedField === 'digest'}
          />
          <FieldRow
            label="Rekor transparency-log entry"
            icon={<ShieldCheck className="w-3.5 h-3.5" />}
            value={data.rekor_log_index || '—'}
            displayValue={data.rekor_log_index || '—'}
            href={data.rekor_log_url}
            testid="build-attestation-rekor"
          />
          <FieldRow
            label="Build recipe"
            icon={<Package className="w-3.5 h-3.5" />}
            value={data.build_recipe}
            displayValue={data.build_recipe}
            testid="build-attestation-recipe"
          />
        </div>

        {data.cosign_verify_command && (
          <div className="mt-6 space-y-2">
            <p className="text-[10px] uppercase tracking-widest text-emerald-400/70">
              Reproduce locally
            </p>
            <ScrollArea className="max-h-32 rounded-xl border border-border bg-black/40">
              <code
                className="block text-[11px] font-mono text-slate-200 p-3 whitespace-pre-wrap break-all select-all"
                data-testid="build-attestation-cosign-cmd"
              >
                {data.cosign_verify_command}
              </code>
            </ScrollArea>
            <Button
              size="sm"
              variant="secondary"
              onClick={() => copyTo(data.cosign_verify_command, 'cosign')}
              data-testid="build-attestation-copy-cosign"
            >
              <Copy className="w-3.5 h-3.5 mr-1" />
              {copiedField === 'cosign' ? 'Copied' : 'Copy cosign command'}
            </Button>
          </div>
        )}

        <p className="mt-6 text-xs text-slate-500 leading-relaxed max-w-3xl">
          Also mirrored at{' '}
          <a
            href="/.well-known/build-attestation.json"
            className="underline decoration-dotted hover:text-slate-300"
            target="_blank"
            rel="noreferrer"
          >
            /.well-known/build-attestation.json
          </a>
          . The Rekor log entry is append-only and public — even we cannot
          retroactively alter a published signature without detection.
        </p>
      </div>
    </section>
  );
};

const FieldRow = ({ label, icon, value, displayValue, href, onCopy, copied, testid }) => (
  <div
    className="rounded-lg border border-border p-2.5 bg-black/20"
    data-testid={testid}
  >
    <p className="text-[10px] uppercase tracking-wide text-slate-500 flex items-center gap-1 mb-1">
      {icon} {label}
    </p>
    <div className="flex items-center justify-between gap-2">
      <p
        className="font-mono text-[11px] text-slate-200 break-all select-all"
        title={value}
      >
        {displayValue}
      </p>
      <div className="flex items-center gap-1 shrink-0">
        {href && (
          <a
            href={href}
            target="_blank"
            rel="noreferrer"
            className="text-slate-500 hover:text-white transition-colors"
            title="Open externally"
          >
            <ExternalLink className="w-3.5 h-3.5" />
          </a>
        )}
        {onCopy && value !== '—' && value !== 'unknown' && (
          <button
            type="button"
            onClick={onCopy}
            className="text-slate-500 hover:text-white transition-colors"
            title="Copy"
          >
            <Copy className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
    </div>
    {copied && <p className="text-[10px] text-emerald-400 mt-1">Copied</p>}
  </div>
);

export default BuildAttestationPanel;
